import { NavContainer, NavLink, NavHeader, NavFooter } from "@lvt/vertical-navbar";
import { SettingsOutlined, BarChart, NewReleasesOutlined } from "@material-ui/icons";
import { OpenInNew, VideoCameraBackOutlined } from "@mui/icons-material";
import EmergencyRecordingIcon from "@mui/icons-material/EmergencyRecording";
import { FC } from "react";

import { Shield, LvtLogoBlack, GuardGateIcon, ForensicSearchIcon, VideoLibraryIcon } from "@/common/components/SVG/Svg";
import { useProductEntitlementsContext } from "@/common/contexts/productEntitlementsContext";
import { useSelector } from "@/store";
import LvtUrls from "@/utilities/types/lvtUrls";

import { useFeatureHub, Flags } from "../Featurehub";
import HelpCenter from "./HelpCenter/HelpCenter";
import MyAccount from "./MyAccount/MyAccount";
import { useShouldShowReports } from "./useShouldShowReports";


interface VerticalNavBarProps {
    urls: LvtUrls;
}

const VerticalNavBar: FC<VerticalNavBarProps> = ({ urls }) => {
    const { permissions, lvtr } = useSelector((state) => state.user);
    const {
        data: { adminAccess, ccAccess, vmAccess, clientCCAccess },
    } = useProductEntitlementsContext();

    const shouldShowReports = useShouldShowReports();

    const { context } = useFeatureHub();

    const shouldShowVideoLibrary = context?.isEnabled(Flags.video_library) && vmAccess;

    return (
        <NavContainer logo={<LvtLogoBlack />} logoUrl={urls.vms}>
            <NavHeader label="VMS" />
            {(permissions?.cameras || permissions?.archives) && (
                <NavLink
                    icon={<VideoCameraBackOutlined />}
                    label="Live Units"
                    path={urls.vms}
                    selected={window.location.href.startsWith(urls.vms) && !window.location.href.includes("alerts")}
                    target="_self"
                />
            )}
            {vmAccess && (
                <NavLink icon={<NewReleasesOutlined />} label="VMS" path={`${urls.nextApp}/vms`} target="_self" />
            )}
            {(permissions?.d3cc || ccAccess) && (
                <NavLink
                    icon={<Shield />}
                    label={"Command Center"}
                    path={ccAccess ? `${urls.nextApp}/alerts` : urls.d3}
                    target="_self"
                />
            )}
            {context?.isEnabled(Flags.guard_gate_all_functionality) && (
                <NavLink
                    icon={<GuardGateIcon />}
                    label="Access Control"
                    path={`${urls.nextApp}/guard-gate`}
                    target="_self"
                />
            )}
            {context?.isEnabled(Flags.forensic_search) && (
                <NavLink
                    icon={<ForensicSearchIcon />}
                    label="Forensic Search"
                    path={`${urls.nextApp}/forensic-search`}
                    target="_self"
                />
            )}
            {shouldShowReports && (
                <NavLink icon={<BarChart />} label="Reports" path={`${urls.nextApp}/reports`} target="_self" />
            )}
            {shouldShowVideoLibrary && (
                <NavLink
                    icon={<VideoLibraryIcon />}
                    label="Video Library"
                    path={`${urls.nextApp}/video-library`}
                    target="_self"
                />
            )}
            {(permissions?.liveUnits || permissions?.locations || permissions?.clients || permissions?.users) && (
                <NavHeader label="Self Service" />
            )}
            {lvtr === "lvt_system_administrator" || lvtr === "partner_with_unit_config" ? (
                <NavLink
                    icon={<EmergencyRecordingIcon />}
                    label="Live Unit Admin"
                    path={`${urls.selfService}`}
                    target="_self"
                />
            ) : null}
            {(ccAccess || vmAccess || clientCCAccess) && (
                <NavLink icon={<OpenInNew />} label="Legacy Settings" path={`${urls.portal}/users`} target="_self" />
            )}
            {(adminAccess || lvtr === "partner_with_unit_config") && (
                <NavLink icon={<SettingsOutlined />} label="Admin" path={`${urls.nextApp}/admin`} target="_self" />
            )}
            <NavFooter>
                <HelpCenter />
                <MyAccount portalUrl={urls.portal} />
            </NavFooter>
        </NavContainer>
    );
};

export default VerticalNavBar;
